import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { firstLoginResetPassword } from '../services/auth';
import { useAuth } from '../AuthContext';
import styles from './FirstLoginReset.module.css';

const FirstLoginReset = () => {
    const { firstLoginToken, setFirstLoginToken, refreshUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const tokenFromState = location?.state?.token;

    useEffect(() => {
        if (tokenFromState && !firstLoginToken) {
            setFirstLoginToken(tokenFromState);
        }
    }, [tokenFromState, firstLoginToken, setFirstLoginToken]);

    const token = useMemo(
        () => firstLoginToken || tokenFromState || '',
        [firstLoginToken, tokenFromState]
    );
    const tokenMissing = !token;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!token) {
            setError('缺少重設 token，請重新登入以取得新連結。');
            return;
        }

        if (!newPassword || !confirmPassword) {
            setError('請輸入並確認新密碼。');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('兩次輸入的新密碼不一致，請重新確認。');
            return;
        }

        setSubmitting(true);
        try {
            await firstLoginResetPassword(token, newPassword);
            setSuccess('密碼已更新，正在導向儀表板...');
            setFirstLoginToken(null);
            setNewPassword('');
            setConfirmPassword('');
            await refreshUser();
            navigate('/', { replace: true });
        } catch (err) {
            const message = err?.message || '密碼更新失敗，請稍後再試。';
            const normalized = message.toLowerCase();
            if (normalized.includes('invalid') && normalized.includes('token')) {
                setError('驗證碼已失效，請重新登入以取得新的 token。');
                setFirstLoginToken(null);
            } else {
                setError(message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const goToLogin = () => {
        setFirstLoginToken(null);
        navigate('/login');
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <p className={styles.eyebrow}>首次登入</p>
                    <h1>重設密碼</h1>
                    <p className={styles.lead}>請設定新的登入密碼以完成帳號啟用。</p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    {tokenMissing && (
                        <div className={styles.alertError}>
                            尚未取得重設 token，請返回登入頁重新登入以取得新的 token。
                        </div>
                    )}
                    {error && <div className={styles.alertError}>{error}</div>}
                    {success && <div className={styles.alertSuccess}>{success}</div>}

                    <label className={styles.field}>
                        <span>新密碼</span>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            placeholder="輸入新密碼"
                            required
                        />
                    </label>
                    <label className={styles.field}>
                        <span>確認新密碼</span>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            placeholder="再次輸入新密碼"
                            required
                        />
                    </label>

                    <button
                        className={styles.submit}
                        type="submit"
                        disabled={submitting || tokenMissing}
                    >
                        {submitting ? '更新中...' : '更新密碼'}
                    </button>
                    <button
                        className={styles.secondary}
                        type="button"
                        onClick={goToLogin}
                        disabled={submitting}
                    >
                        返回登入
                    </button>
                </form>
            </div>
        </div>
    );
};

export default FirstLoginReset;
